import { IconButton, Progress, Spinner } from "@chakra-ui/react";
import axios from "axios";
import React, { useRef, useState } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";
import { FiTrash } from "react-icons/fi";
import { IoRefresh } from "react-icons/io5";
import { TbRefreshAlert } from "react-icons/tb";
import TokenManager from "../../providers/auth/token-manager";
import { formatFileSize, getFileNameAndExtension, truncateText } from "../../utils/string";
// import { useSelector } from "react-redux";
// import { selectAuthToken } from "../../store/slices/auth.slice";

interface FileUploadProps {
    uploadType: "tool_argument"; // /api/v1/output/upload-file
    isDisabled?: boolean;
    onComplete: (data) => void;
    onError: (error) => void;
}

const httpClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL
});

const FileUploadControl: React.FC<FileUploadProps> = ({ onComplete, onError, isDisabled }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string>("");
    const [drag, setDrag] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadPercentage, setUploadPercentage] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(false);
    const tokenManager = TokenManager.getInstance();

    const fileUpload = async (file: File) => {
        if (file) {
            const formData = new FormData();
            formData.append("file", file);

            // set uploading state to true
            setIsUploading(true);
            // upload file to server
            httpClient
                .post(
                    `/api/v1/file/upload-file?access_token=${await tokenManager.getToken()}`,
                    formData,
                    {
                        onUploadProgress: (progressEvent) => {
                            setUploadPercentage(
                                Number(
                                    Math.round(
                                        (progressEvent.loaded * 100) / (progressEvent.total ?? 0)
                                    )
                                )
                            );
                        }
                    }
                )
                .then((response) => {
                    if (response.data.success === false) {
                        throw new Error("could not upload file");
                    }
                    if (typeof onComplete === "function") {
                        setUploadedFileName(response.data.data.file_name);
                        onComplete(response.data.data);
                    }
                    setUploadError(false);
                })
                .catch((error) => {
                    if (typeof onError === "function") {
                        onError(error);
                    }
                    setUploadError(true);
                })
                .finally(() => {
                    setIsUploading(false);
                });
        }
    };

    const fileSelect = () => {
        // if form is disabled exit
        if (isDisabled) {
            return false;
        }
        fileInputRef.current!.click();
    };

    const fileReplaceUpload = async (file: File, oldFileName: string) => {
        const formData = new FormData();

        formData.append("file_names", JSON.stringify([oldFileName]));
        formData.append("files", file);

        setIsUploading(true);
        httpClient
            .post(
                `/api/v1/file/replace-files?access_token=${await tokenManager.getToken()}`,
                formData,
                {
                    onUploadProgress: (progressEvent) => {
                        setUploadPercentage(
                            Number(
                                Math.round(
                                    (progressEvent.loaded * 100) / (progressEvent.total ?? 0)
                                )
                            )
                        );
                    }
                }
            )
            .then((response) => {
                if (response.data.success === false) {
                    throw new Error("could not replace file");
                }
                if (typeof onComplete === "function") {
                    setUploadedFileName(response.data.data.file_names[0]);
                    onComplete(response.data.data);
                }
                setUploadError(false);
            })
            .catch((error) => {
                if (typeof onError === "function") {
                    onError(error);
                }
                setUploadError(true);
            })
            .finally(() => {
                setIsUploading(false);
            });
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        setSelectedFile(file);
        // upload file
        if (file) {
            if (selectedFile) {
                fileReplaceUpload(file, uploadedFileName);
            } else {
                fileUpload(file);
            }
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDrag(true);
    };

    const dragLeave = () => {
        setDrag(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        // if form is disabled exit
        if (isDisabled) {
            return false;
        }

        setDrag(false);
        const file = event.dataTransfer.files && event.dataTransfer.files[0];
        setSelectedFile(file);
        // upload file
        if (file) {
            fileUpload(file);
        }
    };

    const handleDeleteFile = async () => {
        httpClient
            .post(`/api/v1/file/delete-files?access_token=${await tokenManager.getToken()}`, {
                file_names: [uploadedFileName]
            })
            .then((response) => {
                if (response.data.success === false) {
                    throw new Error("could not delete file");
                }
                setSelectedFile(null);
                setUploadedFileName("");
            })
            .catch((error) => {
                if (typeof onError === "function") {
                    onError(error);
                }
            });
    };

    return (
        <div
            className={`file-upload relative ${
                isDisabled && "opacity-65"
            } overflow-hidden border cursor-pointer rounded-[8px] bg-[#404040] border-zinc-500 py-[12px] px-[12px] ${
                drag && !isDisabled && "border-blue-500 shadow-md shadow-blue-500"
            }`}
        >
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
            {selectedFile ? (
                <div className="grid grid-cols-[auto_40px]">
                    <div className="flex gap-[8px]">
                        <div className="icon w-[30px]">
                            <FileIcon
                                extension={getFileNameAndExtension(selectedFile).extension}
                                type="document"
                                color="#c1c1c1"
                                labelColor="#3caf50"
                                {...defaultStyles[getFileNameAndExtension(selectedFile).extension]}
                            />
                        </div>
                        <div>
                            <p className="text-[15px] font-semibold">
                                {truncateText(getFileNameAndExtension(selectedFile).name, 20)}.
                                {getFileNameAndExtension(selectedFile).extension}
                            </p>
                            <p className="text-[12px]">{formatFileSize(selectedFile.size)}</p>
                        </div>
                    </div>
                    <div className="actions flex items-center justify-end pr-[5px]">
                        {!isUploading && (uploadPercentage > 99 || uploadError) && (
                            <>
                                <IconButton
                                    variant={"ghost"}
                                    size="sm"
                                    aria-label="upload"
                                    title="Replace this file"
                                    onClick={fileSelect}
                                >
                                    {uploadError ? (
                                        <TbRefreshAlert className="text-red-500" size="24px" />
                                    ) : (
                                        <IoRefresh className="text-gray-200" size="20px" />
                                    )}
                                </IconButton>
                                <IconButton
                                    variant={"ghost"}
                                    size="sm"
                                    aria-label="delete"
                                    title="Delete file"
                                    onClick={() => handleDeleteFile()}
                                >
                                    <FiTrash className="text-white" size="18px" />
                                </IconButton>
                            </>
                        )}
                        {isUploading && <Spinner size={"sm"} />}
                    </div>
                </div>
            ) : (
                <div
                    className="flex flex-col items-center gap-[6px]"
                    onDragOver={handleDragOver}
                    onDragLeave={dragLeave}
                    onDrop={handleDrop}
                    onClick={fileSelect}
                >
                    <img src="/file.png" alt="file" className="h-[45px] w-[45px] inline-block" />
                    <p className="text-[13.5px]">Select or Drag and drop a file to upload</p>
                </div>
            )}
            {isUploading && (
                <div className="progress absolute left-0 bottom-0 right-0">
                    <Progress
                        value={uploadPercentage}
                        size="xs"
                        colorScheme="blue"
                        className="rounded-none"
                    />
                </div>
            )}
        </div>
    );
};

export default FileUploadControl;
